import React, { ChangeEvent, useEffect, useState, useMemo } from 'react';
import { useLookup } from 'hooks/useLookup';
import Autocomplete from '@mui/material/Autocomplete';
import { CircularProgress, TextField } from '@mui/material';
import { AsyncTaskState } from 'library/async';
import { useDebounce } from 'react-use';

interface LookupSelectProps<T> {
  clientId: string;
  columnName: string;
  dataType: string;
  freeSolo?: boolean;
  getOptionLabel: (opt: T) => string;
  handleSelect: (e: ChangeEvent<{}>, value: T | string | null) => void;
  inputCasing?: 'LOWERCASE' | 'UPPERCASE';
  noOptionsText?: string;
  placeholder?: string;
  queryOperator: string;
  required?: boolean;
  tableId: string;
  title: string;
}
export function LookupSelect<T>({
  clientId,
  columnName,
  dataType,
  freeSolo,
  getOptionLabel,
  handleSelect,
  inputCasing,
  noOptionsText,
  placeholder,
  queryOperator,
  required,
  tableId,
  title,
}: LookupSelectProps<T>) {
  const { getFilteredLookup, getFilteredLookupTask } = useLookup();
  const [open, setOpen] = useState(false);
  const loading = open && getFilteredLookupTask.taskState === AsyncTaskState.IN_PROGRESS;
  const [inputVal, setInputVal] = useState('');
  const [debouncedInputVal, setDebouncedInputVal] = useState('');

  useDebounce(
    () => {
      setDebouncedInputVal(inputVal);
    },
    500,
    [inputVal]
  );

  useEffect(() => {
    let value = debouncedInputVal;
    if (!debouncedInputVal.length) return;
    if (inputCasing) {
      value = inputCasing === 'UPPERCASE' ? value.toUpperCase() : value.toLowerCase();
    }
    getFilteredLookup({
      clientId,
      tableId,
      tableVersion: 0,
      filters: [{ columnName, dataType, queryOperator, value }],
    });
  }, [getFilteredLookup, columnName, queryOperator, clientId, dataType, tableId, inputCasing, debouncedInputVal]);

  const options = useMemo(() => {
    return getFilteredLookupTask.taskState === AsyncTaskState.COMPLETED
      ? (getFilteredLookupTask.result.lookupValues as T[])
      : [];
  }, [getFilteredLookupTask]);

  return (
    <Autocomplete
      autoSelect={freeSolo}
      freeSolo={freeSolo}
      getOptionLabel={getOptionLabel}
      loading={loading}
      noOptionsText={noOptionsText}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      open={open}
      options={options}
      onChange={handleSelect}
      renderInput={params => (
        <TextField
          {...params}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
            if (e.target.value !== '' || e.target.value !== null) {
              setInputVal(e.target.value);
            }
          }}
          label={title}
          variant='outlined'
          placeholder={typeof placeholder === 'string' ? placeholder : 'Type to search...'}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color='inherit' size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      style={{ width: '100%' }}
    />
  );
}
